/**
 * Controles de captura, uno por tipo de pregunta del instrumento.
 *
 * Reglas transversales que vienen del documento:
 *
 *  - Las opciones marcadas `spontaneousOnly` (NS/NC, "ni uno ni otro") NO se
 *    leen en voz alta. Se muestran en un estilo secundario y separadas, para
 *    que el encuestador las registre sólo si la persona las dice por su cuenta.
 *  - Las preguntas espontáneas (N1–N3, C2, C3, P5) NUNCA ofrecen la lista de
 *    personajes ni de partidos: sugerir nombres destruye la medición
 *    espontánea, que es el núcleo estratégico del Bloque 2.
 *  - En las baterías por personaje, la fila muestra el NOMBRE DE TARJETA tal
 *    cual, sin partido ni cargo (§Bloque 3).
 */

import { useMemo, useState } from 'react';

import type { InstrumentOption, InstrumentQuestion } from '../api/client';
import type { DraftAnswer } from '../db/db';
import { answerKey, type Row, type Step } from '../engine/runner';

export interface InputProps {
  step: Step;
  answers: Record<string, DraftAnswer>;
  onAnswer: (key: string, answer: DraftAnswer | null) => void;
  codebook?: { entries: { code: string; label: string; synonyms: string[] }[] } | undefined;
}

/* -------------------------------------------------------------------------- */
/*                                Auxiliares                                  */
/* -------------------------------------------------------------------------- */

function splitOptions(options: readonly InstrumentOption[]): {
  spoken: InstrumentOption[];
  spontaneous: InstrumentOption[];
} {
  return {
    spoken: options.filter((o) => !o.spontaneousOnly),
    spontaneous: options.filter((o) => o.spontaneousOnly),
  };
}

function Marker({ selected, square }: { selected: boolean; square?: boolean }) {
  return (
    <span className={`option__marker${square ? ' option__marker--square' : ''}`} aria-hidden>
      {selected ? '✓' : ''}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                            Opción única                                    */
/* -------------------------------------------------------------------------- */

function SingleChoice({ step, answers, onAnswer }: InputProps) {
  const current = answers[step.answerKey];
  const { spoken, spontaneous } = splitOptions(step.options);

  const select = (option: InstrumentOption) => {
    if (current?.optionCode === option.code) {
      // Segundo toque deselecciona: permite corregir un toque errado sin tener
      // que elegir otra respuesta.
      onAnswer(step.answerKey, null);
      return;
    }
    onAnswer(step.answerKey, {
      questionCode: step.question.code,
      ...(step.row ? { rowCode: step.row.code } : {}),
      optionCode: option.code,
      specifyText: option.requiresSpecify ? (current?.specifyText ?? '') : null,
    });
  };

  const selectedOption = step.options.find((o) => o.code === current?.optionCode);

  return (
    <>
      <div className="options">
        {spoken.map((option) => (
          <button
            key={option.code}
            type="button"
            className={`option${current?.optionCode === option.code ? ' option--selected' : ''}`}
            onClick={() => select(option)}
          >
            <Marker selected={current?.optionCode === option.code} />
            <span className="option__label">
              {option.label}
              {option.interviewerHint ? (
                <span className="option__hint">{option.interviewerHint}</span>
              ) : null}
            </span>
          </button>
        ))}
      </div>

      {spontaneous.length > 0 ? (
        <>
          <p className="faint" style={{ margin: '14px 0 6px' }}>
            No leer. Registrar sólo si la persona lo dice por su cuenta:
          </p>
          <div className="options">
            {spontaneous.map((option) => (
              <button
                key={option.code}
                type="button"
                className={`option option--spontaneous${
                  current?.optionCode === option.code ? ' option--selected' : ''
                }`}
                onClick={() => select(option)}
              >
                <Marker selected={current?.optionCode === option.code} />
                <span className="option__label">{option.label}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {selectedOption?.requiresSpecify ? (
        <div className="field" style={{ marginTop: 14 }}>
          <label className="field__label" htmlFor="specify">
            Especifique
          </label>
          <input
            id="specify"
            className="input"
            value={current?.specifyText ?? ''}
            onChange={(e) =>
              onAnswer(step.answerKey, {
                ...current!,
                questionCode: step.question.code,
                optionCode: selectedOption.code,
                specifyText: e.target.value,
              })
            }
            autoComplete="off"
          />
        </div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                          Opción múltiple con tope                          */
/* -------------------------------------------------------------------------- */

/**
 * M1 pide hasta 2 medios y marcar el PRINCIPAL; M4 admite varios tipos.
 * Cada selección se guarda como una respuesta propia, con su propio código, de
 * modo que la exportación tenga una columna por opción y no una lista empacada
 * en un solo campo.
 */
function MultiChoice({ step, answers, onAnswer }: InputProps) {
  const max = step.question.maxSelections ?? 99;
  const prefix = step.answerKey;

  const selected = useMemo(
    () =>
      Object.entries(answers)
        .filter(([key]) => key === prefix || key.startsWith(`${prefix}::`))
        .map(([key, value]) => ({ key, value }))
        .filter((x) => Boolean(x.value.optionCode)),
    [answers, prefix],
  );

  const isSelected = (code: string) => selected.some((s) => s.value.optionCode === code);
  const primaryCode = selected.find((s) => s.value.isPrimary)?.value.optionCode ?? null;
  const needsPrimary = step.question.code === 'M1';

  const toggle = (option: InstrumentOption) => {
    const existing = selected.find((s) => s.value.optionCode === option.code);
    if (existing) {
      onAnswer(existing.key, null);
      return;
    }
    if (selected.length >= max) return;
    onAnswer(`${prefix}::${option.code}`, {
      questionCode: step.question.code,
      optionCode: option.code,
      specifyText: option.requiresSpecify ? '' : null,
      isPrimary: needsPrimary && selected.length === 0 ? true : null,
    });
  };

  const setPrimary = (code: string) => {
    for (const item of selected) {
      onAnswer(item.key, { ...item.value, isPrimary: item.value.optionCode === code });
    }
  };

  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        {selected.length} de {max} seleccionadas
        {selected.length >= max ? ' · tope alcanzado' : ''}
      </p>

      <div className="options">
        {step.options.map((option) => {
          const on = isSelected(option.code);
          const blocked = !on && selected.length >= max;
          return (
            <button
              key={option.code}
              type="button"
              className={`option${on ? ' option--selected' : ''}`}
              onClick={() => toggle(option)}
              disabled={blocked}
            >
              <Marker selected={on} square />
              <span className="option__label">{option.label}</span>
            </button>
          );
        })}
      </div>

      {needsPrimary && selected.length > 1 ? (
        <div style={{ marginTop: 16 }}>
          <p className="field__label">¿Cuál es el principal?</p>
          <div className="row row--wrap">
            {selected.map((item) => {
              const label =
                step.options.find((o) => o.code === item.value.optionCode)?.label ??
                item.value.optionCode;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`chip${primaryCode === item.value.optionCode ? ' chip--selected' : ''}`}
                  onClick={() => setPrimary(item.value.optionCode!)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                       Abierta jerarquizada (N1)                            */
/* -------------------------------------------------------------------------- */

/**
 * Menciones espontáneas en casillas numeradas: N1 (ordenada) y P5 (sin orden).
 *
 * En N1 el orden ES un dato: §10.1.1 define el top-of-mind como la PRIMERA
 * mención y la notoriedad espontánea total como cualquier mención. Por eso la
 * primera casilla va rotulada y no se pueden reordenar.
 *
 * En P5 las menciones valen igual —§10.2.4 sólo cuenta si el partido aparece—
 * y la casilla es nada más una forma de separar dos respuestas. Rotularla como
 * «top-of-mind» aquí haría que el encuestador creyera que el orden importa.
 *
 * Lo común a las dos, y la razón de que compartan componente: la lista NO se
 * lee. Se captura textual y se codifica en gabinete (§9.5).
 */
function MentionSlots({ step, answers, onAnswer }: InputProps) {
  const ordered = step.question.kind === 'ranked-open';
  const max = step.question.maxSelections ?? (ordered ? 5 : 2);
  const slots = Array.from({ length: max }, (_, i) => i + 1);

  return (
    <>
      <div className="notice notice--warning">
        <span className="notice__title">
          {ordered ? 'No sugerir ningún nombre' : 'No leer la lista'}
        </span>
        {ordered
          ? 'Captura textualmente lo que diga la persona, en el orden en que lo diga. La primera mención es el top-of-mind.'
          : `Captura textualmente lo que diga la persona. Admite hasta ${max} menciones y las dos valen igual: el orden no importa.`}
      </div>

      <div className="stack">
        {slots.map((rank) => {
          const key = answerKey(step.question.code, null, rank);
          const value = answers[key]?.textValue ?? '';
          // Sólo se habilita la casilla siguiente si la anterior tiene texto:
          // evita menciones en posición 3 con la 2 vacía, que haría ambiguo el
          // orden de mención.
          const previousFilled =
            rank === 1 ||
            Boolean(answers[answerKey(step.question.code, null, rank - 1)]?.textValue?.trim());
          return (
            <div className="field" key={rank} style={{ marginBottom: 0 }}>
              <label className="field__label" htmlFor={`mention-${rank}`}>
                {ordered && rank === 1 ? '1ª mención (top-of-mind)' : `${rank}ª mención`}
              </label>
              <input
                id={`mention-${rank}`}
                className="input"
                value={value}
                disabled={!previousFilled}
                placeholder={
                  previousFilled ? (ordered ? 'Nombre tal como lo dijo' : 'Tal como lo dijo') : '—'
                }
                onChange={(e) => {
                  const text = e.target.value;
                  onAnswer(
                    key,
                    text.trim() === ''
                      ? null
                      : {
                          questionCode: step.question.code,
                          rankOrder: rank,
                          textValue: text,
                        },
                  );
                }}
                autoComplete="off"
                autoCapitalize="words"
              />
            </div>
          );
        })}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                      Abierta con codificación en campo                     */
/* -------------------------------------------------------------------------- */

/**
 * C2 y C3 · abiertas con libro de códigos.
 *
 * Se captura el TEXTO LITERAL primero y, por separado, se ofrece el código.
 * Nunca al revés: si el encuestador viera la lista de códigos antes de escuchar
 * la respuesta, la induciría. El texto literal se conserva siempre para la
 * recodificación de gabinete de §9.5.
 */
function OpenWithCodebook({ step, answers, onAnswer, codebook }: InputProps) {
  const current = answers[step.answerKey];
  const [query, setQuery] = useState('');
  const text = current?.textValue ?? '';

  const normalize = (v: string) =>
    v
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');

  const suggestions = useMemo(() => {
    if (!codebook) return [];
    const needle = normalize(query || text);
    if (needle.trim().length < 2) return codebook.entries.slice(0, 8);
    return codebook.entries
      .filter(
        (entry) =>
          normalize(entry.label).includes(needle) ||
          entry.synonyms.some((s) => normalize(s).includes(needle)),
      )
      .slice(0, 8);
  }, [codebook, query, text]);

  return (
    <>
      <div className="field">
        <label className="field__label" htmlFor="open-text">
          Respuesta textual
        </label>
        <textarea
          id="open-text"
          className="textarea"
          value={text}
          placeholder="Escribe lo que dijo la persona"
          onChange={(e) => {
            const value = e.target.value;
            setQuery(value);
            onAnswer(
              step.answerKey,
              value.trim() === ''
                ? null
                : {
                    questionCode: step.question.code,
                    ...(step.row ? { rowCode: step.row.code } : {}),
                    textValue: value,
                    optionCode: current?.optionCode ?? null,
                  },
            );
          }}
        />
      </div>

      {codebook && text.trim() !== '' ? (
        <div>
          <p className="field__label">Código (opcional; se puede ajustar en gabinete)</p>
          <div className="row row--wrap">
            {suggestions.map((entry) => (
              <button
                key={entry.code}
                type="button"
                className={`chip${current?.optionCode === entry.code ? ' chip--selected' : ''}`}
                onClick={() =>
                  onAnswer(step.answerKey, {
                    ...current!,
                    questionCode: step.question.code,
                    textValue: text,
                    optionCode: current?.optionCode === entry.code ? null : entry.code,
                  })
                }
              >
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Abierta simple                               */
/* -------------------------------------------------------------------------- */

function OpenText({ step, answers, onAnswer }: InputProps) {
  const current = answers[step.answerKey];
  const max = step.question.maxSelections;

  // M2 admite hasta 3 menciones.
  if (max && max > 1) {
    return (
      <div className="stack">
        {Array.from({ length: max }, (_, i) => i + 1).map((rank) => {
          const key = answerKey(step.question.code, null, rank);
          return (
            <div className="field" key={rank} style={{ marginBottom: 0 }}>
              <label className="field__label" htmlFor={`open-${rank}`}>
                Mención {rank}
              </label>
              <input
                id={`open-${rank}`}
                className="input"
                value={answers[key]?.textValue ?? ''}
                onChange={(e) =>
                  onAnswer(
                    key,
                    e.target.value.trim() === ''
                      ? null
                      : {
                          questionCode: step.question.code,
                          rankOrder: rank,
                          textValue: e.target.value,
                        },
                  )
                }
                autoComplete="off"
              />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="field">
      <textarea
        className="textarea"
        value={current?.textValue ?? ''}
        placeholder="Captura textual"
        onChange={(e) =>
          onAnswer(
            step.answerKey,
            e.target.value.trim() === ''
              ? null
              : {
                  questionCode: step.question.code,
                  ...(step.row ? { rowCode: step.row.code } : {}),
                  textValue: e.target.value,
                },
          )
        }
      />
      {step.question.allowRefusal ? (
        <button
          type="button"
          className="option option--spontaneous"
          style={{ marginTop: 10 }}
          onClick={() =>
            onAnswer(step.answerKey, {
              questionCode: step.question.code,
              ...(step.row ? { rowCode: step.row.code } : {}),
              optionCode: 'ns_nc',
              textValue: null,
            })
          }
        >
          <Marker selected={current?.optionCode === 'ns_nc'} />
          <span className="option__label">No sabe / no contestó (no leer)</span>
        </button>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Numérica                                  */
/* -------------------------------------------------------------------------- */

function Numeric({ step, answers, onAnswer }: InputProps) {
  const current = answers[step.answerKey];
  const value = current?.numericValue;

  return (
    <div className="field">
      <input
        className="input input--numeric"
        type="number"
        inputMode="numeric"
        min={step.question.min}
        max={step.question.max}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onAnswer(step.answerKey, null);
            return;
          }
          const parsed = Number.parseInt(raw, 10);
          if (!Number.isFinite(parsed)) return;
          onAnswer(step.answerKey, {
            questionCode: step.question.code,
            numericValue: parsed,
          });
        }}
      />
      {step.question.min !== undefined && step.question.max !== undefined ? (
        <p className="faint" style={{ marginTop: 6 }}>
          Entre {step.question.min} y {step.question.max}
        </p>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Escala 0-10 (E1)                               */
/* -------------------------------------------------------------------------- */

function Scale({ step, answers, onAnswer }: InputProps) {
  const current = answers[step.answerKey];
  const min = step.question.min ?? 0;
  const max = step.question.max ?? 10;
  const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  return (
    <>
      <div className="scale">
        {values.map((value) => (
          <button
            key={value}
            type="button"
            className={`scale__item${current?.numericValue === value ? ' scale__item--selected' : ''}`}
            onClick={() =>
              onAnswer(
                step.answerKey,
                current?.numericValue === value
                  ? null
                  : { questionCode: step.question.code, numericValue: value },
              )
            }
          >
            {value}
          </button>
        ))}
      </div>
      <div className="scale__anchors">
        <span>{min} · seguro no voy a votar</span>
        <span>{max} · seguro voy a votar</span>
      </div>
      {step.question.allowRefusal ? (
        <button
          type="button"
          className={`option option--spontaneous${current?.optionCode === 'ns_nc' ? ' option--selected' : ''}`}
          style={{ marginTop: 12 }}
          onClick={() =>
            onAnswer(step.answerKey, {
              questionCode: step.question.code,
              optionCode: 'ns_nc',
              numericValue: null,
            })
          }
        >
          <Marker selected={current?.optionCode === 'ns_nc'} />
          <span className="option__label">No sabe / no contestó (no leer)</span>
        </button>
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Sí / No                                     */
/* -------------------------------------------------------------------------- */

function YesNo(props: InputProps) {
  return <SingleChoice {...props} />;
}

/* -------------------------------------------------------------------------- */
/*                          Batería en matriz                                 */
/* -------------------------------------------------------------------------- */

/**
 * Batería con la escala repetida por fila.
 *
 * §Bloque 3 indica aplicar POR PREGUNTA y no por personaje: primero I1 para
 * todos, después I2 para los conocidos, y así. Por eso cada batería es una sola
 * pantalla con todas sus filas, y no una pantalla por personaje: es más rápido
 * y reduce el efecto halo.
 */
function Matrix({ step, answers, onAnswer }: InputProps) {
  const { spoken, spontaneous } = splitOptions(step.options);
  const answered = step.rows.filter((row) =>
    Boolean(answers[answerKey(step.question.code, row.code)]?.optionCode),
  ).length;

  const setRow = (row: Row, option: InstrumentOption) => {
    const key = answerKey(step.question.code, row.code);
    const current = answers[key];
    onAnswer(
      key,
      current?.optionCode === option.code
        ? null
        : {
            questionCode: step.question.code,
            rowCode: row.code,
            optionCode: option.code,
          },
    );
  };

  return (
    <>
      <div className="row row--between" style={{ marginBottom: 12 }}>
        <span className="muted">
          {answered} de {step.rows.length} contestadas
        </span>
        {answered === step.rows.length ? (
          <span className="badge badge--ok">completa</span>
        ) : null}
      </div>

      <div>
        {step.rows.map((row) => {
          const current = answers[answerKey(step.question.code, row.code)];
          return (
            <div
              className={`matrix__row${current?.optionCode ? ' matrix__row--answered' : ''}`}
              key={row.code}
            >
              <div className="matrix__label">{row.label}</div>
              <div className="matrix__scale">
                {spoken.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    className={`chip${current?.optionCode === option.code ? ' chip--selected' : ''}`}
                    onClick={() => setRow(row, option)}
                  >
                    {option.label}
                  </button>
                ))}
                {spontaneous.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    className={`chip chip--muted${current?.optionCode === option.code ? ' chip--selected' : ''}`}
                    onClick={() => setRow(row, option)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Despachador                                  */
/* -------------------------------------------------------------------------- */

export function QuestionInput(props: InputProps) {
  const { step } = props;

  if (step.isMatrix) return <Matrix {...props} />;

  switch (step.question.kind) {
    case 'yes-no':
      return <YesNo {...props} />;
    case 'single':
    case 'consent':
    case 'matrix-single':
      return <SingleChoice {...props} />;
    case 'multi':
      return <MultiChoice {...props} />;
    case 'numeric':
      return <Numeric {...props} />;
    case 'scale':
      return <Scale {...props} />;
    case 'ranked-open':
    case 'open-multi':
      return <MentionSlots {...props} />;
    case 'open':
    case 'matrix-open':
      return props.codebook ? <OpenWithCodebook {...props} /> : <OpenText {...props} />;
    default:
      return <OpenText {...props} />;
  }
}

/** Texto introductorio del bloque y nota para el encuestador. */
export function QuestionHeader({ step }: { step: Step }) {
  const { question, block } = step;
  return (
    <>
      <div className="block-tag">
        Bloque {block.id} · {block.title}
      </div>

      {block.readAloudIntro && isFirstQuestionOfBlock(step) ? (
        <div className="readaloud">{block.readAloudIntro}</div>
      ) : null}

      <h2 className="question-text">
        <span className="faint mono" style={{ marginRight: 8 }}>
          {question.code}
          {step.row ? ` · ${step.row.code}` : ''}
        </span>
        {step.text}
      </h2>

      {question.interviewerNote ? (
        <div className="interviewer-note">
          <span className="interviewer-note__label">Instrucción · no leer</span>
          {question.interviewerNote}
        </div>
      ) : null}
    </>
  );
}

function isFirstQuestionOfBlock(step: Step): boolean {
  return step.block.questions[0]?.code === step.question.code && !step.row;
}
